/**
 * Sales Order Detail Page.
 *
 * Full SO detail with line items, confirm/payment actions, commission display,
 * and gross profit breakdown.
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  DollarSign,
  FileText,
  Mail,
  ChevronRight,
  Download,
  Package,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import ActionDrawer from "@/components/ui/ActionDrawer";
import InlineEdit from "@/components/ui/InlineEdit";
import OrderChargesDrawer from "@/components/ui/OrderChargesDrawer";
import TaxDrawer from "@/components/ui/TaxDrawer";
import PaymentPanel from "@/features/payments/PaymentPanel";
import {
  useAddSOLine,
  useAddSOLineCharge,
  useApplySOSchemes,
  useConfirmSalesOrder,
  useAddSOOrderCharge,
  useDeleteSOLineCharge,
  useDeleteSOOrderCharge,
  useRecordSOPayment,
  useRemoveSOLine,
  useSalesOrder,
  useUpdateSOLine,
  useUpdateSalesOrder,
} from "@/features/sales/api";
import {
  AddChargeRow,
  ChargeSubRow,
  type ChargeRowLayout,
} from "@/components/ui/ChargeSubRow";
import { lineMargin, orderMargin } from "@/utils/margin";
import {
  effectiveTaxRate,
  formatRatePct,
  splitChargeRows,
  TAX_CHIP_CLASS,
  taxSource,
  type TaxBasis,
} from "@/utils/taxDisplay";
import type { SOLine, SOLineCreate, SOLineUpdate } from "@/types/sales-order";
import { toast } from "@/stores/toastStore";
import { useAssets } from "@/features/inventory/api";
import { usePermissions } from "@/hooks/usePermissions";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import DocumentHeader from "@/components/ui/DocumentHeader";
import Input from "@/components/ui/Input";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { useOrgConfig } from "@/features/settings/api";
import { downloadFromApi } from "@/utils/downloadBlob";
import ComposeEmail from "@/components/ui/ComposeEmail";
import { useBusinessLocations } from "@/features/locations/api";

/**
 * Where a charge row sits in this table's ten columns.
 *
 * # | Description | Category | Qty | Unit | Discount | Amount | Cost | Margin |
 * actions. The charge spans Description+Category, leaves Qty/Unit/Discount empty,
 * puts its amount in Amount, and trails through Cost/Margin/actions.
 */
const SO_CHARGE_LAYOUT: ChargeRowLayout = {
  leading: 1,
  contentColSpan: 2,
  filler: 3,
  trailing: 3,
};

/**
 * How each commission basis reads in the totals stack.
 *
 * Mirrors VALID_COMMISSION_BASES in app/services/sales_order_service.py. An
 * unrecognised value falls back rather than rendering a raw enum at the customer.
 */
const COMMISSION_BASIS_LABELS: Record<string, string> = {
  discounted_subtotal: "Discounted subtotal",
  sale_price: "Sale price",
  gross_profit: "Gross profit",
};

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(v);

/** The house section-header idiom — see VendorsPage.tsx. */
function SectionHeader({
  icon: Icon,
  title,
}: {
  icon: React.ElementType;
  title: string;
}) {
  return (
    <div className="mb-3 flex items-center gap-2 border-b border-[var(--border-subtle)] pb-2">
      <Icon className="h-4 w-4 text-[var(--text-tertiary)]" />
      <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
        {title}
      </span>
    </div>
  );
}

/** Surface the server's reason rather than a generic failure. */
function errorDetail(err: unknown): string | undefined {
  const axiosErr = err as { response?: { data?: { detail?: unknown } } };
  const detail = axiosErr?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  // FastAPI validation errors arrive as a list of {msg, loc}.
  if (Array.isArray(detail)) {
    const first = detail[0] as { msg?: string } | undefined;
    if (first?.msg) return first.msg;
  }
  return undefined;
}

/**
 * Add either kind of line.
 *
 * An asset-backed line sells one specific tracked asset and is always one unit; a
 * manual line bills something that is not inventory and needs a description and
 * may carry any quantity. The mode toggle exists because those are different
 * things to say, not two ways of saying one thing.
 */
function AddSOLineForm({
  assets,
  isAdding,
  onAdd,
  onCancel,
}: {
  assets: Array<{ id: string; sku: string; name: string; status: string }>;
  isAdding: boolean;
  onAdd: (data: SOLineCreate) => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<"asset" | "manual">("asset");
  const [assetId, setAssetId] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unitPrice, setUnitPrice] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [discountType, setDiscountType] = useState<"" | "percent" | "amount">(
    "",
  );
  const [discountValue, setDiscountValue] = useState("");

  const price = parseFloat(unitPrice);
  const valid =
    !isNaN(price) &&
    price >= 0 &&
    (mode === "asset" ? !!assetId : !!description.trim());

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    onAdd({
      ...(mode === "asset"
        ? { asset_id: assetId }
        : {
            description: description.trim(),
            category: category || undefined,
            quantity: parseInt(quantity, 10) || 1,
            unit_cost: unitCost ? parseFloat(unitCost) : undefined,
          }),
      unit_price: price,
      ...(discountType
        ? {
            discount_type: discountType,
            discount_value: parseFloat(discountValue) || 0,
          }
        : {}),
    });
  };

  const fieldClass =
    "mt-1 block w-full h-[34px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-[var(--text-primary)]";
  const labelCls =
    "block text-[11px] font-medium uppercase tracking-wider text-[var(--text-tertiary)]";

  return (
    <form
      onSubmit={submit}
      className="border-b border-[var(--border-default)] bg-[var(--surface-sunken)] p-4"
    >
      <div className="mb-3 inline-flex rounded-[var(--radius-md)] border border-[var(--border-default)] p-0.5">
        {(["asset", "manual"] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`h-7 rounded-[var(--radius-sm)] px-3 text-xs font-medium transition-colors ${
              mode === m
                ? "bg-[var(--color-interactive-primary)] text-white"
                : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            }`}
          >
            {m === "asset" ? "Asset" : "Manual line"}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        {mode === "asset" ? (
          <div className="sm:col-span-2">
            <label className={labelCls}>Asset</label>
            <select
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
              required
              className={fieldClass}
            >
              <option value="">Select asset...</option>
              {assets
                .filter((a) => a.status !== "Sold")
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.sku} — {a.name}
                  </option>
                ))}
            </select>
          </div>
        ) : (
          <>
            <div className="sm:col-span-2">
              <label className={labelCls}>Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is being billed"
                required
                className={fieldClass}
              />
              <p className="mt-1 text-[11px] text-[var(--text-tertiary)]">
                This is what the customer reads on the invoice.
              </p>
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Optional"
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelCls}>Quantity</label>
              <input
                type="number"
                min="1"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className={fieldClass}
              />
            </div>
          </>
        )}

        <div>
          <label className={labelCls}>Unit price</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
            placeholder="0.00"
            required
            className={`${fieldClass} text-right tabular-nums`}
          />
        </div>

        {mode === "manual" && (
          <div>
            <label className={labelCls}>Unit cost</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value)}
              placeholder="Optional"
              className={`${fieldClass} text-right tabular-nums`}
            />
            <p className="mt-1 text-[11px] text-[var(--text-tertiary)]">
              Without a cost this line reports no margin, rather than full
              margin.
            </p>
          </div>
        )}

        <div>
          <label className={labelCls}>Discount</label>
          <select
            value={discountType}
            onChange={(e) =>
              setDiscountType(e.target.value as "" | "percent" | "amount")
            }
            className={fieldClass}
          >
            <option value="">None</option>
            <option value="percent">Percentage</option>
            <option value="amount">Fixed amount</option>
          </select>
        </div>
        {discountType && (
          <div>
            <label className={labelCls}>
              {discountType === "percent" ? "Discount %" : "Discount $"}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder="0.00"
              className={`${fieldClass} text-right tabular-nums`}
            />
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end gap-2 border-t border-[var(--border-subtle)] pt-3">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" isLoading={isAdding} disabled={!valid}>
          Add line
        </Button>
      </div>
    </form>
  );
}

/**
 * Edit one line after it has been added.
 *
 * Charges are edited in the table itself, as sub-rows under their parent, so this
 * drawer deliberately does not repeat them — two editors over the same rows is
 * how they drift. What it owns is the line's own commercial terms.
 *
 * Quantity appears only on a manual line. The service rejects a quantity on an
 * asset line, since an asset is identified individually, so offering the field
 * would be offering an edit that 400s.
 */
function SOLineEditDrawer({
  line,
  isOpen,
  isSaving,
  onClose,
  onSave,
}: {
  line: SOLine | null;
  isOpen: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSave: (data: SOLineUpdate) => void;
}) {
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unitPrice, setUnitPrice] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [discountType, setDiscountType] = useState<"" | "percent" | "amount">(
    "",
  );
  const [discountValue, setDiscountValue] = useState("");

  // Reload the form whenever a different line is opened — the drawer is mounted
  // once and reused, so without this it would show the previous line's figures.
  useEffect(() => {
    if (!line) return;
    setDescription(line.description ?? "");
    setQuantity(String(line.quantity));
    setUnitPrice(String(line.unit_price));
    setUnitCost(line.unit_cost === null ? "" : String(line.unit_cost));
    setDiscountType(line.discount_type ?? "");
    setDiscountValue(line.discount_type ? String(line.discount_value) : "");
  }, [line]);

  if (!line) return null;
  const isManual = line.asset_id === null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      description: description.trim() || undefined,
      ...(isManual ? { quantity: parseInt(quantity, 10) || 1 } : {}),
      unit_price: parseFloat(unitPrice) || 0,
      ...(isManual && unitCost !== ""
        ? { unit_cost: parseFloat(unitCost) }
        : {}),
      // Sending null clears the discount; the service reads an explicit null as
      // "remove it" rather than "leave it alone".
      discount_type: discountType || null,
      discount_value: discountType ? parseFloat(discountValue) || 0 : 0,
    });
  };

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Edit line"
      description={isManual ? "Manual line" : "Asset line"}
      icon={Pencil}
      size="md"
    >
      <form onSubmit={submit} className="space-y-6">
        <div>
          <SectionHeader icon={FileText} title="Item" />
          <div className="space-y-3">
            <Input
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={isManual ? "What is being billed" : "Asset identity"}
              required={isManual}
            />
            {isManual && (
              <Input
                label="Quantity"
                type="number"
                min="1"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            )}
          </div>
        </div>

        <div>
          <SectionHeader icon={DollarSign} title="Price" />
          <div className="space-y-3 rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3">
            <Input
              label="Unit price"
              type="number"
              step="0.01"
              min="0"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              required
            />
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-[var(--text-tertiary)]">
                Discount
              </label>
              <div className="mt-1 flex gap-2">
                <select
                  value={discountType}
                  onChange={(e) =>
                    setDiscountType(e.target.value as "" | "percent" | "amount")
                  }
                  className="h-[34px] flex-1 rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-[var(--text-primary)]"
                >
                  <option value="">None</option>
                  <option value="percent">Percentage</option>
                  <option value="amount">Fixed amount</option>
                </select>
                {discountType && (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder={discountType === "percent" ? "%" : "0.00"}
                    className="h-[34px] w-28 rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-right text-sm tabular-nums text-[var(--text-primary)]"
                  />
                )}
              </div>
              {line.discount_amount > 0 && (
                <p className="mt-1 text-[11px] text-[var(--text-tertiary)]">
                  Currently discounting {fmt(Number(line.discount_amount))}.
                </p>
              )}
            </div>
            {isManual && (
              <div>
                <Input
                  label="Unit cost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                  placeholder="Optional"
                />
                <p className="mt-1 text-[11px] text-[var(--text-tertiary)]">
                  Without a cost this line reports no margin, rather than full
                  margin.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-[var(--border-subtle)] pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save line
          </Button>
        </div>
      </form>
    </ActionDrawer>
  );
}

const statusVariant: Record<
  string,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  Draft: "default",
  Confirmed: "info",
  Invoiced: "warning",
  Paid: "success",
  Cancelled: "danger",
};

function SalesOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: so, isLoading } = useSalesOrder(id);
  const { data: config } = useOrgConfig();
  const { data: businessLocations } = useBusinessLocations();
  const addLineMutation = useAddSOLine(id || "");
  const removeLineMutation = useRemoveSOLine(id || "");
  const confirmMutation = useConfirmSalesOrder(id || "");
  const paymentMutation = useRecordSOPayment(id || "");
  const updateSOMutation = useUpdateSalesOrder(id || "");
  const addLineChargeMutation = useAddSOLineCharge(id || "");
  const deleteLineChargeMutation = useDeleteSOLineCharge(id || "");
  const applySchemesMutation = useApplySOSchemes(id || "");
  const addOrderChargeMutation = useAddSOOrderCharge(id || "");
  const deleteOrderChargeMutation = useDeleteSOOrderCharge(id || "");
  const updateLineMutation = useUpdateSOLine(id || "");

  const { data: assetData } = useAssets({ pageSize: 100, status: undefined });

  const [showAddLine, setShowAddLine] = useState(false);
  const [expandedLineId, setExpandedLineId] = useState<string | null>(null);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showTaxDrawer, setShowTaxDrawer] = useState(false);
  const [showChargesDrawer, setShowChargesDrawer] = useState(false);

  const [showPayment, setShowPayment] = useState(false);
  const [editingCommission, setEditingCommission] = useState(false);
  const [commissionOverride, setCommissionOverride] = useState("");
  const { hasPermission } = usePermissions();
  const canOverrideCommission = hasPermission("sales.override_commission");
  const [payMethod, setPayMethod] = useState("wire");
  const [payRef, setPayRef] = useState("");
  const [showEmail, setShowEmail] = useState(false);

  if (isLoading) return <LoadingSpinner className="py-24" size="lg" />;
  if (!so)
    return (
      <div className="py-24 text-center">
        <p className="text-[var(--text-secondary)]">Sales order not found.</p>
      </div>
    );

  const isDraft = so.status === "Draft";
  const canPay = so.status === "Confirmed" || so.status === "Invoiced";

  /**
   * Download the invoice the server renders.
   *
   * Not an `<a href>`: the API authenticates on a Bearer header the axios
   * interceptor adds, which a plain link cannot carry, so the browser would get a
   * 401 and save nothing.
   */
  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      await downloadFromApi(
        `/api/v1/sales-orders/${so.id}/invoice.pdf`,
        `${so.status === "Draft" ? "Pro-Forma" : "Invoice"}-${so.so_number}.pdf`,
      );
    } catch (err) {
      toast.error(
        "Could not download the PDF",
        err instanceof Error ? err.message : undefined,
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await paymentMutation.mutateAsync({
        payment_method: payMethod,
        payment_reference: payRef || undefined,
      });
      setShowPayment(false);
      toast.success("Payment recorded");
    } catch {
      toast.error("Failed to record payment");
    }
  };

  // ── Derived money, all read from the server's figures rather than recomputed.
  const subtotal = Number(so.subtotal);
  const discountAmount = Number(so.discount_amount);
  const discountedSubtotal = subtotal - discountAmount;
  const feesTotal = Number(so.fees_total);

  // Tax is charged on the discounted subtotal plus whatever charges are flagged
  // taxable — both the ones sitting on lines and the order-level fee rows. Using
  // the bare subtotal here would report a rate the engine never applied.
  const lineChargesTotal = so.line_items.reduce(
    (sum, l) => sum + Number(l.line_fees),
    0,
  );
  const taxableLineCharges = so.line_items.reduce(
    (sum, l) =>
      sum +
      (l.charges || []).reduce(
        (lineSum, c) => lineSum + (c.is_taxable ? Number(c.amount) : 0),
        0,
      ),
    0,
  );
  const { fee: orderFeeRows } = splitChargeRows(so.tax_lines || []);
  const taxableOrderFees = orderFeeRows.reduce(
    (sum, r) => sum + (r.is_taxable ? Number(r.applied_tax) : 0),
    0,
  );
  const taxBasis: TaxBasis = {
    subtotal,
    discountAmount,
    taxableCharges: taxableLineCharges + taxableOrderFees,
  };
  const taxRate = effectiveTaxRate(so.tax_lines || [], taxBasis);
  const taxSrc = taxSource(so.tax_lines || []);

  const margin = orderMargin(so.line_items, so.status, discountedSubtotal);

  // Which figure commission is calculated on is an organisation setting, and it
  // moves with the discount, so the row says so. Before a Commission record
  // exists the org default is what will be used; after, the record's own basis is
  // the truth, since the setting may have changed since.
  const commissionBasisLabel =
    COMMISSION_BASIS_LABELS[
      so.commissions[0]?.basis ??
        config?.sales?.default_commission_basis ??
        "discounted_subtotal"
    ] ?? "discounted subtotal";

  return (
    <div className="page-content space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate("/sales")}
            className="mt-1 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-[var(--text-primary)]">
                {so.so_number}
              </h2>
              <Badge variant={statusVariant[so.status] || "default"}>
                {so.status}
              </Badge>
            </div>
            {so.order_date && (
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                {new Date(so.order_date).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {isDraft && so.line_items.length > 0 && (
            <Button
              size="sm"
              onClick={() =>
                confirmMutation.mutate(undefined, {
                  onSuccess: () =>
                    toast.success(
                      "Sale confirmed",
                      `${so.so_number} confirmed.`,
                    ),
                  onError: () => toast.error("Failed to confirm sale"),
                })
              }
              isLoading={confirmMutation.isPending}
            >
              <Check className="h-4 w-4" /> Confirm Sale
            </Button>
          )}
          {canPay && (
            <Button size="sm" onClick={() => setShowPayment(!showPayment)}>
              <DollarSign className="h-4 w-4" /> Record Payment
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            isLoading={isDownloading}
            onClick={handleDownloadPdf}
          >
            <Download className="h-4 w-4" />{" "}
            {so.status === "Draft" ? "Pro Forma PDF" : "Invoice PDF"}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowEmail(true)}
          >
            <Mail className="h-4 w-4" /> Email Invoice
          </Button>
        </div>
      </div>

      {(() => {
        const soLoc = businessLocations?.find((l) => l.id === so.location_id);
        const locAddr = soLoc
          ? {
              name: soLoc.name,
              street1: soLoc.address?.street1,
              street2: soLoc.address?.street2,
              city: soLoc.address?.city,
              state: soLoc.address?.state,
              zip: soLoc.address?.zip,
              phone: soLoc.phone || undefined,
              email: soLoc.email || undefined,
            }
          : undefined;
        return (
          <DocumentHeader
            documentType="Sales Order"
            documentNumber={so.so_number}
            status={so.status}
            statusVariant={statusVariant[so.status] || "default"}
            date={so.order_date}
            dueDate={so.delivery_date}
            dueDateLabel="Delivery"
            party={so.customer_name ? { name: so.customer_name } : undefined}
            extra={{
              Salesperson: so.salesperson_name || undefined,
              Payment: so.payment_method || undefined,
            }}
            locationAddress={locAddr}
          />
        );
      })()}

      {/* Payment Drawer */}
      <ActionDrawer
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        title="Record Payment"
        description={`Record a payment for ${so.so_number}.`}
        icon={DollarSign}
        size="sm"
      >
        <form onSubmit={handlePayment} className="space-y-4">
          <div>
            <label className="block text-[13px] font-medium text-[var(--text-primary)]">
              Payment Method
            </label>
            <select
              value={payMethod}
              onChange={(e) => setPayMethod(e.target.value)}
              className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
            >
              {["wire", "check", "cash", "financing", "credit_card", "ach"].map(
                (m) => (
                  <option key={m} value={m}>
                    {m.replace("_", " ")}
                  </option>
                ),
              )}
            </select>
          </div>
          <Input
            label="Reference"
            value={payRef}
            onChange={(e) => setPayRef(e.target.value)}
            placeholder="Check #, Wire ref..."
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowPayment(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              isLoading={paymentMutation.isPending}
            >
              Submit Payment
            </Button>
          </div>
        </form>
      </ActionDrawer>

      {/* Line Items */}
      <Card padding="none">
        <div className="flex items-center justify-between border-b border-[var(--border-default)] px-4 py-3">
          <h3 className="text-sm font-medium text-[var(--text-primary)]">
            Items ({so.line_items.length})
          </h3>
          {isDraft && (
            <div className="flex items-center gap-2">
              {so.line_items.length > 0 && (
                <Button
                  size="sm"
                  variant="secondary"
                  isLoading={applySchemesMutation.isPending}
                  onClick={() =>
                    applySchemesMutation.mutate(undefined, {
                      onSuccess: ({ summary }) => {
                        // The server tells us what landed, so this reports rather
                        // than infers.
                        const parts: string[] = [];
                        if (summary.line_charges_created > 0) {
                          parts.push(
                            `${summary.line_charges_created} line charge${
                              summary.line_charges_created === 1 ? "" : "s"
                            } (${fmt(Number(summary.line_fees_total))})`,
                          );
                        }
                        if (Number(summary.order_fees_total) > 0) {
                          parts.push(
                            `${fmt(Number(summary.order_fees_total))} order charges`,
                          );
                        }
                        if (Number(summary.order_tax_total) > 0) {
                          parts.push(
                            `${fmt(Number(summary.order_tax_total))} tax`,
                          );
                        }
                        toast.success(
                          "Schemes applied",
                          parts.length
                            ? `Applied ${parts.join(" + ")}.`
                            : "No matching schemes for this order.",
                        );
                      },
                      onError: (err) =>
                        toast.error(
                          "Failed to apply schemes",
                          errorDetail(err),
                        ),
                    })
                  }
                >
                  <DollarSign className="h-4 w-4" /> Apply Schemes
                </Button>
              )}
              <Button size="sm" onClick={() => setShowAddLine(!showAddLine)}>
                {showAddLine ? (
                  "Cancel"
                ) : (
                  <>
                    <Plus className="h-4 w-4" /> Add Line
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {showAddLine && (
          <AddSOLineForm
            assets={assetData?.items ?? []}
            isAdding={addLineMutation.isPending}
            onCancel={() => setShowAddLine(false)}
            onAdd={(data) =>
              addLineMutation.mutate(data, {
                onSuccess: () => {
                  setShowAddLine(false);
                  toast.success("Line added");
                },
                onError: (err) =>
                  toast.error("Failed to add line", errorDetail(err)),
              })
            }
          />
        )}

        {so.line_items.length > 0 ? (
          /* Ten columns will not fit on a narrow viewport. min-w makes the
             wrapper actually scroll — without it `w-full` shrinks the table and
             the cells crush instead. */
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-sm">
              <thead>
                <tr className="border-b border-[var(--border-default)] bg-[var(--surface-sunken)] text-left text-xs font-medium text-[var(--text-secondary)]">
                  <th className="w-10 px-3 py-2"></th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="w-16 px-4 py-2 text-right">Qty</th>
                  <th className="w-28 px-4 py-2 text-right">Unit price</th>
                  <th className="w-24 px-4 py-2 text-right">Discount</th>
                  <th className="w-28 px-4 py-2 text-right">Amount</th>
                  <th className="w-24 px-4 py-2 text-right">Cost</th>
                  <th className="w-24 px-4 py-2 text-right">Margin</th>
                  <th className="w-16 px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {so.line_items.map((line, index) => {
                  const margin = lineMargin(line, so.status);
                  const isExpanded = expandedLineId === line.id;
                  const chargeCount = line.charges?.length ?? 0;
                  const isManual = line.asset_id === null;

                  return (
                    <React.Fragment key={line.id}>
                      <tr className="border-b border-[var(--border-subtle)]">
                        <td className="px-3 py-2.5 align-top">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-[var(--text-tertiary)]">
                              {index + 1}
                            </span>
                            {isManual ? (
                              <FileText
                                className="h-3 w-3 text-[var(--text-tertiary)]"
                                aria-label="Manual line"
                              />
                            ) : (
                              <Package
                                className="h-3 w-3 text-[var(--text-tertiary)]"
                                aria-label="Asset line"
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          {line.asset_id ? (
                            <Link
                              to={`/inventory/${line.asset_id}`}
                              className="text-xs text-[var(--color-interactive-primary)] hover:underline"
                            >
                              {line.description || "View asset"}
                            </Link>
                          ) : (
                            /* A manual line bills something untracked, so its
                               description is its only identity. */
                            <span className="text-xs text-[var(--text-primary)]">
                              {line.description || "—"}
                            </span>
                          )}
                          {chargeCount > 0 && (
                            <div className="mt-0.5 text-[10px] text-[var(--text-warning)]">
                              + {fmt(Number(line.line_fees))} charges
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs capitalize text-[var(--text-secondary)]">
                          {line.category || "—"}
                        </td>
                        <td className="px-4 py-2.5 text-right text-xs tabular-nums text-[var(--text-secondary)]">
                          {/* An asset is identified individually, so its line is
                              always one unit and the service rejects any other. */}
                          <span
                            title={
                              isManual
                                ? undefined
                                : "An asset-backed line is always one unit"
                            }
                          >
                            {line.quantity}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          {fmt(Number(line.unit_price))}
                        </td>
                        <td className="px-4 py-2.5 text-right text-xs tabular-nums text-[var(--text-secondary)]">
                          {line.discount_type ? (
                            <span
                              title={`−${fmt(Number(line.discount_amount))}`}
                            >
                              {line.discount_type === "percent"
                                ? `${Number(line.discount_value)}%`
                                : fmt(Number(line.discount_value))}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right font-medium tabular-nums">
                          {fmt(Number(line.total_price))}
                        </td>
                        <td
                          className="px-4 py-2.5 text-right text-xs tabular-nums text-[var(--text-secondary)]"
                          title={margin.explanation ?? undefined}
                        >
                          {margin.cogs === null ? "—" : fmt(margin.cogs)}
                        </td>
                        <td
                          className={`px-4 py-2.5 text-right text-xs font-medium tabular-nums ${
                            margin.grossProfit === null
                              ? "text-[var(--text-tertiary)]"
                              : margin.grossProfit >= 0
                                ? "text-[var(--text-success)]"
                                : "text-[var(--text-danger)]"
                          }`}
                          title={margin.explanation ?? undefined}
                        >
                          {margin.state === "none"
                            ? "n/a"
                            : margin.grossProfit === null
                              ? "—"
                              : fmt(margin.grossProfit)}
                        </td>
                        <td className="px-2 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() =>
                                setExpandedLineId(isExpanded ? null : line.id)
                              }
                              title={
                                chargeCount > 0
                                  ? `${chargeCount} charge${chargeCount === 1 ? "" : "s"}`
                                  : "Charges"
                              }
                              className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </button>
                            {isDraft && (
                              <button
                                onClick={() => setEditingLineId(line.id)}
                                title="Edit line"
                                className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            )}
                            {isDraft && (
                              <button
                                onClick={() =>
                                  removeLineMutation.mutate(line.id, {
                                    onSuccess: () =>
                                      toast.success("Line removed"),
                                    onError: () =>
                                      toast.error("Failed to remove line"),
                                  })
                                }
                                className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <>
                          {(line.charges || []).map((charge) => (
                            <ChargeSubRow
                              key={charge.id}
                              charge={charge}
                              layout={SO_CHARGE_LAYOUT}
                              canDelete={isDraft}
                              onDelete={() =>
                                deleteLineChargeMutation.mutate(
                                  { lineId: line.id, chargeId: charge.id },
                                  {
                                    onSuccess: () =>
                                      toast.success("Charge removed"),
                                    onError: () =>
                                      toast.error("Failed to remove charge"),
                                  },
                                )
                              }
                            />
                          ))}
                          {isDraft && (
                            <AddChargeRow
                              layout={SO_CHARGE_LAYOUT}
                              askTaxable
                              isAdding={addLineChargeMutation.isPending}
                              onAdd={(data) =>
                                addLineChargeMutation.mutate(
                                  { lineId: line.id, ...data },
                                  {
                                    onSuccess: () =>
                                      toast.success("Charge added"),
                                    onError: (err) =>
                                      toast.error(
                                        "Failed to add charge",
                                        errorDetail(err),
                                      ),
                                  },
                                )
                              }
                            />
                          )}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">
            No items yet. Add an asset or a manual line to this sale.
          </p>
        )}

        {/* ── Totals, right-aligned like a paper invoice ── */}
        <div className="border-t border-[var(--border-default)] px-6 py-4">
          <div className="flex justify-end">
            <div className="w-64 space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-secondary)]">Subtotal</span>
                <span className="tabular-nums font-medium text-[var(--text-primary)]">
                  {fmt(subtotal)}
                </span>
              </div>

              {/* Shown in Draft even at zero, because it is editable there and a
                  row that appears only once used is a row nobody finds. */}
              {(discountAmount > 0 || isDraft) && (
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-[var(--text-secondary)]">
                    Discount
                    {so.discount_type === "percent" &&
                      Number(so.discount_value) > 0 && (
                        <span className="text-xs text-[var(--text-tertiary)]">
                          ({Number(so.discount_value)}%)
                        </span>
                      )}
                  </span>
                  {isDraft ? (
                    <InlineEdit
                      value={String(Number(so.discount_value))}
                      displayValue={
                        discountAmount > 0 ? `− ${fmt(discountAmount)}` : "—"
                      }
                      onSave={(val) => {
                        const v = parseFloat(val) || 0;
                        updateSOMutation.mutate(
                          {
                            // An amount unless the order is already set to a
                            // percentage — editing the figure should not silently
                            // switch which kind of discount this is.
                            discount_type:
                              v > 0
                                ? so.discount_type === "percent"
                                  ? "percent"
                                  : "amount"
                                : null,
                            discount_value: v,
                          },
                          {
                            onError: (err) =>
                              toast.error(
                                "Failed to set discount",
                                errorDetail(err),
                              ),
                          },
                        );
                      }}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      editable={isDraft}
                      className="tabular-nums text-[var(--text-danger)]"
                      inputClassName="w-24 text-right"
                    />
                  ) : (
                    <span className="tabular-nums text-[var(--text-danger)]">
                      − {fmt(discountAmount)}
                    </span>
                  )}
                </div>
              )}

              {(feesTotal > 0 || isDraft) && (
                <button
                  type="button"
                  onClick={() => setShowChargesDrawer(true)}
                  title="View and edit charges"
                  className="group -mx-1 flex w-full items-center justify-between gap-2 rounded-[var(--radius-sm)] px-1 py-0.5 text-sm transition-colors hover:bg-[var(--surface-sunken)]"
                >
                  <span className="text-[var(--text-warning)]">Fees</span>
                  <span className="flex shrink-0 items-center gap-0.5 tabular-nums text-[var(--text-warning)]">
                    {fmt(feesTotal)}
                    <ChevronRight className="h-3.5 w-3.5 text-[var(--text-tertiary)] opacity-0 transition-opacity group-hover:opacity-100" />
                  </span>
                </button>
              )}

              <button
                type="button"
                onClick={() => setShowTaxDrawer(true)}
                title="View tax breakdown and options"
                className="group -mx-1 flex w-full items-center justify-between gap-2 rounded-[var(--radius-sm)] px-1 py-0.5 text-sm transition-colors hover:bg-[var(--surface-sunken)]"
              >
                <span className="flex min-w-0 items-center gap-1.5 text-[var(--text-secondary)]">
                  Tax
                  {taxRate != null && taxRate > 0 && (
                    <span className="text-xs text-[var(--text-tertiary)]">
                      ({formatRatePct(taxRate)})
                    </span>
                  )}
                  {taxSrc && taxSrc.tone !== "auto" && (
                    <span
                      className={`inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-medium ${TAX_CHIP_CLASS[taxSrc.tone]}`}
                    >
                      {taxSrc.label}
                    </span>
                  )}
                </span>
                <span className="flex shrink-0 items-center gap-0.5 tabular-nums text-[var(--text-primary)]">
                  {fmt(Number(so.tax))}
                  <ChevronRight className="h-3.5 w-3.5 text-[var(--text-tertiary)] opacity-0 transition-opacity group-hover:opacity-100" />
                </span>
              </button>

              {/* Legacy: the order schema refuses shipping now that charges
                  exist, so old orders can show it but nothing new can set it. */}
              {Number(so.shipping) > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span
                    className="text-[var(--text-secondary)]"
                    title="Set before charges existed. New shipping goes in as a charge."
                  >
                    Shipping <span className="text-[10px]">(legacy)</span>
                  </span>
                  <span className="tabular-nums text-[var(--text-primary)]">
                    {fmt(Number(so.shipping))}
                  </span>
                </div>
              )}

              {Number(so.trade_in_credit) > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[var(--text-secondary)]">Trade-in</span>
                  <span className="tabular-nums text-[var(--text-danger)]">
                    − {fmt(Number(so.trade_in_credit))}
                  </span>
                </div>
              )}

              <div className="mt-1.5 border-t border-[var(--border-default)] pt-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[var(--text-primary)]">
                    Total
                  </span>
                  <span className="text-lg font-bold tabular-nums text-[var(--text-primary)]">
                    {fmt(Number(so.total))}
                  </span>
                </div>
              </div>

              {/* Neither of these is a component of Total, so they sit below the
                  line rather than inside the customer-facing arithmetic. */}
              <div className="mt-3 space-y-1.5 border-t border-[var(--border-subtle)] pt-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                  Internal
                </p>
                <div className="flex items-center justify-between text-sm">
                  <span
                    className="text-[var(--text-secondary)]"
                    title={
                      margin.partial
                        ? "Some lines have no recorded cost and are left out of this figure."
                        : undefined
                    }
                  >
                    Gross profit
                    {margin.partial && (
                      <span className="ml-1 text-[10px] text-[var(--text-tertiary)]">
                        (partial)
                      </span>
                    )}
                    {margin.percent != null && (
                      <span className="ml-1 text-xs text-[var(--text-tertiary)]">
                        ({(margin.percent * 100).toFixed(1)}%)
                      </span>
                    )}
                  </span>
                  <span className="tabular-nums text-[var(--text-success)]">
                    {margin.grossProfit === null
                      ? "—"
                      : fmt(margin.grossProfit)}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span
                    className="flex items-center gap-1.5 text-[var(--text-secondary)]"
                    title={`Calculated on the ${commissionBasisLabel.toLowerCase()}`}
                  >
                    Commission
                    {so.commission_rate != null && (
                      <span className="text-xs text-[var(--text-tertiary)]">
                        ({(so.commission_rate * 100).toFixed(1)}%)
                      </span>
                    )}
                    {/* A discounted order's commission is unexplainable without
                        the basis, which is an org setting. */}
                    <span className="text-[10px] text-[var(--text-tertiary)]">
                      on {commissionBasisLabel.toLowerCase()}
                    </span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    {so.commissions.length > 0 && so.commissions[0] && (
                      <Badge
                        variant={
                          so.commissions[0].status === "paid"
                            ? "success"
                            : "warning"
                        }
                      >
                        {so.commissions[0].status}
                      </Badge>
                    )}
                    {editingCommission ? (
                      <>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={commissionOverride}
                          onChange={(e) =>
                            setCommissionOverride(e.target.value)
                          }
                          className="h-7 w-20 rounded-[var(--radius-md)] border border-[var(--border-default)] px-2 text-right text-sm tabular-nums"
                          placeholder="%"
                          autoFocus
                        />
                        <button
                          onClick={() =>
                            updateSOMutation.mutate(
                              {
                                commission_rate: commissionOverride
                                  ? parseFloat(commissionOverride) / 100
                                  : null,
                              },
                              {
                                onSuccess: () => {
                                  setEditingCommission(false);
                                  toast.success("Commission updated");
                                },
                                onError: (err) =>
                                  toast.error(
                                    "Failed to update commission",
                                    errorDetail(err),
                                  ),
                              },
                            )
                          }
                          title="Save"
                          className="text-[var(--color-interactive-primary)]"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="tabular-nums text-[var(--color-interactive-primary)]">
                          {so.commission_amount > 0
                            ? fmt(so.commission_amount)
                            : "—"}
                        </span>
                        {isDraft && canOverrideCommission && (
                          <button
                            onClick={() => {
                              setCommissionOverride(
                                so.commission_rate
                                  ? (so.commission_rate * 100).toFixed(2)
                                  : "",
                              );
                              setEditingCommission(true);
                            }}
                            title="Override commission rate"
                            className="text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)]"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Payments */}
      <div>
        <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3">
          Payments
        </h3>
        <PaymentPanel
          entityType="sales_order"
          entityId={so.id}
          lineItems={so.line_items}
        />
      </div>

      <TaxDrawer
        isOpen={showTaxDrawer}
        onClose={() => setShowTaxDrawer(false)}
        entityType="sales_order"
        entityId={so.id}
        taxLines={so.tax_lines || []}
        basis={taxBasis}
        taxTotal={Number(so.tax)}
        canEdit={isDraft}
        queryKey={["sales-orders", so.id]}
      />

      <OrderChargesDrawer
        isOpen={showChargesDrawer}
        onClose={() => setShowChargesDrawer(false)}
        taxLines={so.tax_lines || []}
        lineChargesTotal={lineChargesTotal}
        canEdit={isDraft}
        isAdding={addOrderChargeMutation.isPending}
        isDeleting={deleteOrderChargeMutation.isPending}
        onAdd={(data) =>
          addOrderChargeMutation.mutate(data, {
            onSuccess: () => toast.success("Charge added"),
            onError: (err) =>
              toast.error("Failed to add charge", errorDetail(err)),
          })
        }
        onDelete={(chargeId) =>
          deleteOrderChargeMutation.mutate(chargeId, {
            onSuccess: () => toast.success("Charge removed"),
            onError: (err) =>
              toast.error("Failed to remove charge", errorDetail(err)),
          })
        }
      />

      <SOLineEditDrawer
        line={so.line_items.find((l) => l.id === editingLineId) ?? null}
        isOpen={editingLineId !== null}
        isSaving={updateLineMutation.isPending}
        onClose={() => setEditingLineId(null)}
        onSave={(data) =>
          updateLineMutation.mutate(
            { lineId: editingLineId!, data },
            {
              onSuccess: () => {
                setEditingLineId(null);
                toast.success("Line updated");
              },
              onError: (err) =>
                toast.error("Failed to update line", errorDetail(err)),
            },
          )
        }
      />

      <ComposeEmail
        isOpen={showEmail}
        onClose={() => setShowEmail(false)}
        defaultTo={so.customer_email ?? ""}
        defaultToName={so.customer_name ?? ""}
        defaultSubject={`${so.status === "Draft" ? "Pro Forma Invoice" : "Invoice"} ${so.so_number}`}
        entityType="invoice"
        entityId={so.id}
        attachDocument
      />
    </div>
  );
}

export default SalesOrderDetailPage;
